import axios from 'axios';
import { useDispatch } from 'react-redux';

import API_ENDPOINTS from '../const/ApiEndpoints';
import IDPStrategies from '../utils/IDPStrategies';
import { commonSlice } from '../state/reducers/common';

const useAuth = () => {
  const dispatch = useDispatch();
  /**
   * Handles user login by sending a POST request to the authentication endpoint for Admin Panel.
   * @param {Object} payload - The login credentials, typically containing username and password.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   * @typedef {Object} LoginResponse
   * @property {string} access_token - The access token returned by the server upon successful
   * login.
   */
  const loginRequest = (payload) => {
    return axios
      .post(API_ENDPOINTS.BASE_URL + API_ENDPOINTS.AUTH_LOGIN, payload)
      .then((response) => {
        if (response?.data?.access_token) {
          localStorage.setItem('ACCESS_TOKEN', response.data.access_token);
        }
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Handles user logout by sending a POST request to the logout endpoint for Admin Panel.
   * @param {Object} payload - The logout request payload, typically containing user information.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const logoutRequest = (payload) => {
    return axios
      .post(API_ENDPOINTS.BASE_URL + API_ENDPOINTS.AUTH_LOGOUT, payload)
      .then((response) => {
        dispatch(commonSlice.actions.reset());
        localStorage.removeItem('ACCESS_TOKEN');
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Fetches all tenants from the server.
   * @returns {Promise} A promise that resolves with the response containing tenant data.
   * @throws {Error} Throws an error if the request fails.
   */
  const getAllTenants = () => {
    return axios
      .get(API_ENDPOINTS.BASE_URL + API_ENDPOINTS.GET_ALL_TENANTS)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Sends a login request for a specific product.
   * @param {Object} headers - The headers to include in the request.
   * @param {Object} payload - The login credentials for the product.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const loginProductRequest = (headers, payload) => {
    return axios
      .post(API_ENDPOINTS.BASE_URL + API_ENDPOINTS.LOGIN_PRODUCT, payload, {
        headers,
        withCredentials: true,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Fetches the list of products associated with a specific tenant.
   * @param {Object} headers - The headers to include in the request.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const getProductsAssociatedToTenant = (headers) => {
    return axios
      .get(
        API_ENDPOINTS.BASE_URL + API_ENDPOINTS.GET_PRODUCT_LIST_WITH_TENANT,
        {
          headers,
        }
      )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Retrieves necessary data using an authorization code for normal product login.
   * @param {Object} headers - The headers to include in the request.
   * @param {Object} payload - The request payload containing the authorization code.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const getTokenFromAuthCode = (headers, payload) => {
    return axios
      .post(
        API_ENDPOINTS.BASE_URL + API_ENDPOINTS.GET_TOKEN_WITH_AUTH_CODE,
        payload,
        {
          headers,
        }
      )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Retrieves necessary data using an authorization code for product IDP login.
   * @param {Object} headers - The headers to include in the request.
   * @param {Object} payload - The request payload containing the authorization code.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const getTokenFromAuthCodeForIDP = (headers, payload) => {
    return axios
      .post(
        API_ENDPOINTS.BASE_URL + API_ENDPOINTS.IDP_GET_TOKEN_WITH_CODE,
        payload,
        {
          headers,
        }
      )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Sends a request to reset the password for a product side user.
   * @param {Object} headers - The headers to include in the request.
   * @param {Object} payload - The request payload containing user information.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const forgotPassword = (headers, payload) => {
    return axios
      .post(API_ENDPOINTS.BASE_URL + API_ENDPOINTS.FORGOT_PASSWORD, payload, {
        headers,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Sends a request to reset the password for a product side user.
   * @param {Object} headers - The headers to include in the request.
   * @param {Object} payload - The request payload containing user information.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const forgotPasswordSubadmin = (payload) => {
    return axios
      .post(
        API_ENDPOINTS.BASE_URL + API_ENDPOINTS.FORGOT_PASSWORD_SUBADMIN,
        payload
      )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Sends a request to reset the password for a product side user.
   * @param {Object} headers - The headers to include in the request.
   * @param {Object} payload - The request payload containing user information.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const resetPasswordSubadmin = (headers, payload) => {
    return axios
      .post(
        API_ENDPOINTS.BASE_URL + API_ENDPOINTS.RESET_PASSWORD_SUBADMIN,
        payload,
        {
          headers,
        }
      )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Sends a request to reset the password for a product side user.
   * @param {Object} headers - The headers to include in the request.
   * @param {Object} payload - The request payload containing user information.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const resetPassword = (headers, payload) => {
    return axios
      .post(API_ENDPOINTS.BASE_URL + API_ENDPOINTS.RESET_PASSWORD, payload, {
        headers,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Fetches the list of Identity Providers (IDPs) for product login.
   * @param {Object} headers - The headers to include in the request.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const getIDPList = (headers) => {
    return axios
      .get(API_ENDPOINTS.BASE_URL + API_ENDPOINTS.IDP_WITHOUT_AUTH, {
        headers,
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Initiates the login process with an Identity Provider (IDP) for product login.
   * @param {string} productDomain - The product domain for the login request.
   * @param {Object} state - The state object to include in the request.
   * @param {Object} headers - The headers to include in the request.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const loginIDP = async (productDomain, state = {}, headers = {}) => {
    const { data } = await axios.get(
      API_ENDPOINTS.BASE_URL + API_ENDPOINTS.IDP_LOGIN,
      {
        headers,
        params: { product_domain: productDomain },
      }
    );
    const { redirect_url: IDPRedirectUrl, type } = data;
    if (!IDPRedirectUrl) {
      throw new Error('IDP did not return a redirect_url');
    }
    const handler = IDPStrategies[type];
    if (!handler) {
      throw new Error(`Unsupported IDP type "${type}"`);
    }
    const finalUrl = handler(IDPRedirectUrl, state, productDomain);
    window.location.href = finalUrl;
    return data;
  };
  /**
   * Retrieves CSRF token for authentication.
   * @param {Object} headers - The headers to include in the request, e.g., tenant ID.
   * @returns {Promise} A promise that resolves with the response containing CSRF token.
   * @throws {Error} Throws an error if the request fails.
   */
  const getCsrfToken = (headers = {}) => {
    return axios
      .get(API_ENDPOINTS.BASE_URL + API_ENDPOINTS.CSRF_TOKEN, {
        headers,
        withCredentials: true,
      })
      .then((response) => response)
      .catch((error) => {
        throw error;
      });
  };

  /**
   * Checks if there is an active session for the user.
   * @param {Object} headers - The headers to include in the request.
   * @param {Object} payload - The request payload containing session information.
   * @returns {Promise} A promise that resolves with the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const checkActiveSession = (headers, payload) => {
    return axios
      .post(
        API_ENDPOINTS.BASE_URL + API_ENDPOINTS.CHECK_ACTIVE_SESSION,
        payload,
        {
          headers,
          withCredentials: true,
        }
      )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };

  return {
    loginRequest,
    logoutRequest,
    getAllTenants,
    loginProductRequest,
    getProductsAssociatedToTenant,
    getTokenFromAuthCode,
    getTokenFromAuthCodeForIDP,
    forgotPassword,
    resetPassword,
    forgotPasswordSubadmin,
    resetPasswordSubadmin,
    getIDPList,
    loginIDP,
    getCsrfToken,
    checkActiveSession,
  };
};

export default useAuth;
